import React from "react";
import { GamesListAndDetail } from "./GamesListAndDetail";
import { Path } from "../Path";
import { SellsheetListAndDetail } from "../sellsheets/SellsheetListAndDetail";
import { RulesListAndDetail } from "../rules/RulesListAndDetail";
import { s2GameId } from "@seabrookstudios/types";
import { AppWithSideMenu } from "../core/AppWithSideMenu";
import { PublisherInterestListAndDetail } from "../games/PublisherInterestListAndDetail";
import {
  GameComponentConfigurationQuotesBreadcrumb,
  GameComponentConfigurationsBreadcrumb,
  GameLanesBreadcrumb,
  GamesBreadcrumb,
  makeBreadcrumbs,
  MyGames,
  SelectedGameBreadcrumbs,
  SelectedGameContractsBreadcrumbs,
  SelectedGameDesignerBreadcrumbs,
  SelectedGamePublisherBreadcrumbs,
  SelectedGamePublisherInterestBreadcrumbs,
  SelectedGameRulesBreadcrumbs,
  SelectedGameSelectedDesignerBreadcrumbs,
  SelectedGameSellsheetsBreadcrumbs,
} from "../core/Breadcrumbs";
import { DesignerListAndDetail } from "./designers/DesignerListAndDetail";
import { s2UserId } from "@seabrookstudios/types";
import { s2PublisherId } from "../util/type-conversion";
import { ListAndDetail as ContractListAndDetail } from "./contracts/ListAndDetail";
import { ListAndDetail as TasksListAndDetail } from "./tasks/ListAndDetail";
import { ListAndDetail as ComponentListAndDetail } from "./components/Concept";
import { ListAndDetail as QuoteListAndDetail } from "./components/quotes/ComponentQuotesConcept";
import { FreightListAndDetail } from "./freight/FreightConcept";
import { FinancialModelling } from "./financial-modelling/Concept";
import { RoyaltiesListAndDetail } from "./royalties/ListAndDetail";
import { ListAndDetail as GameSlugsListAndDetail } from "../game-slugs/ListAndDetail";
import { GameQuickLinks } from "./GameQuickLinks";
import { ListAndDetail as ConventionsListAndDetail } from "./conventions/ListAndDetail";
import { PublisherResearchListAndDetail } from "./publisher-interest/PublisherResearchListAndDetail";
import { GameResearchListAndDetail } from "./game-research/GameResearchListAndDetail";

export const routes = [
  {
    index: true,
    path: Path.games().pathname,
    element: (
      <AppWithSideMenu>
        <GamesListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      prefer: "left",
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).link().pathname,
    element: (
      <AppWithSideMenu>
        <GamesListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: SelectedGameBreadcrumbs,
      prefer: "right",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).publishers().pathname,
    element: (
      <AppWithSideMenu>
        <PublisherInterestListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      /**
       * @param {object} props
       * @param {import("@seabrookstudios/types").GameId} props.gameId
       */
      crumb: SelectedGamePublisherBreadcrumbs,
      prefer: "left",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).publisher(s2PublisherId(":publisherId")).pathname,
    element: (
      <AppWithSideMenu>
        <PublisherInterestListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: SelectedGamePublisherInterestBreadcrumbs,
      prefer: "right",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).designers().pathname,
    element: (
      <AppWithSideMenu>
        <DesignerListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: SelectedGameDesignerBreadcrumbs,
      prefer: "left",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).designer(s2UserId(":userId")).pathname,
    element: (
      <AppWithSideMenu>
        <DesignerListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: SelectedGameSelectedDesignerBreadcrumbs,
      prefer: "right",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).sellsheets().pathname,
    element: (
      <AppWithSideMenu>
        <SellsheetListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: SelectedGameSellsheetsBreadcrumbs,
      prefer: "left",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).sellsheet(
      /** @type {import("@seabrookstudios/pitch2table-core").AttachmentId} */ (":attachmentId")
    ).pathname,
    element: (
      <AppWithSideMenu>
        <SellsheetListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: SelectedGameSellsheetsBreadcrumbs,
      prefer: "right",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).rules().pathname,
    element: (
      <AppWithSideMenu>
        <RulesListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: SelectedGameRulesBreadcrumbs,
      prefer: "left",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).rule(
      /** @type {import("@seabrookstudios/pitch2table-core").AttachmentId} */ (":attachmentId")
    ).pathname,
    element: (
      <AppWithSideMenu>
        <RulesListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: SelectedGameRulesBreadcrumbs,
      prefer: "right",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).contracts().pathname,
    element: (
      <AppWithSideMenu>
        <ContractListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: SelectedGameContractsBreadcrumbs,
      prefer: "left",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).contract(
      /** @type {import("@seabrookstudios/pitch2table-core").AttachmentId} */ (":attachmentId")
    ).pathname,
    element: (
      <AppWithSideMenu>
        <ContractListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: SelectedGameContractsBreadcrumbs,
      prefer: "right",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).tasks().pathname,
    element: (
      <AppWithSideMenu>
        <TasksListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: makeBreadcrumbs("Tasks", ({ gameId }) => Path.game(gameId).tasks(), [MyGames, GamesBreadcrumb]),
      prefer: "left",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).components().pathname,
    element: (
      <AppWithSideMenu>
        <ComponentListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: makeBreadcrumbs("Components", ({ gameId }) => Path.game(gameId).components(), [MyGames, GamesBreadcrumb]),
      prefer: "left",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).component(":configurationId").link().pathname,
    element: (
      <AppWithSideMenu>
        <ComponentListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: makeBreadcrumbs(
        "TBC",
        ({ gameId, configurationId }) => Path.game(gameId).component(configurationId).link(),
        [MyGames, GamesBreadcrumb, GameComponentConfigurationsBreadcrumb]
      ),
      prefer: "right",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).component(":configurationId").quotes().pathname,
    element: (
      <AppWithSideMenu>
        <QuoteListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: makeBreadcrumbs(
        "Quotes",
        ({ gameId, configurationId }) => Path.game(s2GameId(gameId)).component(configurationId).quotes(),
        [MyGames, GamesBreadcrumb, GameComponentConfigurationsBreadcrumb]
      ),
      prefer: "left",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).component(":configurationId").quote(":quoteId").pathname,
    element: (
      <AppWithSideMenu>
        <QuoteListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: makeBreadcrumbs(
        "QUOTE",
        ({ gameId, configurationId, quoteId }) => Path.game(gameId).component(configurationId).quote(quoteId),
        [MyGames, GamesBreadcrumb, GameComponentConfigurationsBreadcrumb, GameComponentConfigurationQuotesBreadcrumb]
      ),
      prefer: "right",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).lanes().pathname,
    element: (
      <AppWithSideMenu>
        <FreightListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: makeBreadcrumbs("Lanes", ({ gameId }) => Path.game(gameId).lanes(), [MyGames, GamesBreadcrumb]),
      prefer: "left",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).lane(":laneId").link().pathname,
    element: (
      <AppWithSideMenu>
        <FreightListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: makeBreadcrumbs("TBC", ({ gameId, laneId }) => Path.game(gameId).lane(laneId).link(), [
        MyGames,
        GamesBreadcrumb,
        GameLanesBreadcrumb,
      ]),
      prefer: "right",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).financialModelling().pathname,
    element: (
      <AppWithSideMenu>
        <FinancialModelling />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: makeBreadcrumbs("Financial Modelling", ({ gameId }) => Path.game(gameId).financialModelling(), [
        MyGames,
        GamesBreadcrumb,
      ]),
      prefer: "left",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).royalties().pathname,
    element: (
      <AppWithSideMenu>
        <RoyaltiesListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: makeBreadcrumbs("Royalties", ({ gameId }) => Path.game(gameId).royalties(), [MyGames, GamesBreadcrumb]),
      prefer: "left",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).royalty(":royaltyId").pathname,
    element: (
      <AppWithSideMenu>
        <RoyaltiesListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: makeBreadcrumbs("Royalties", ({ gameId }) => Path.game(gameId).royalties(), [MyGames, GamesBreadcrumb]),
      prefer: "right",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).slugs().pathname,
    element: (
      <AppWithSideMenu>
        <GameSlugsListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: makeBreadcrumbs("Visibility", ({ gameId }) => Path.game(gameId).slugs(), [MyGames, GamesBreadcrumb]),
      prefer: "left",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).conventions().pathname,
    element: (
      <AppWithSideMenu>
        <ConventionsListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: makeBreadcrumbs("Conventions", ({ gameId }) => Path.game(gameId).conventions(), [
        MyGames,
        GamesBreadcrumb,
      ]),
      prefer: "left",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).research().pathname,
    element: (
      <AppWithSideMenu>
        <PublisherResearchListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: makeBreadcrumbs("Publisher Research", ({ gameId }) => Path.game(gameId).research(), [
        MyGames,
        GamesBreadcrumb,
      ]),
      prefer: "left",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).gameResearch().pathname,
    element: (
      <AppWithSideMenu>
        <GameResearchListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: makeBreadcrumbs("Game Research", ({ gameId }) => Path.game(gameId).gameResearch(), [
        MyGames,
        GamesBreadcrumb,
      ]),
      prefer: "left",
      quickLinks: GameQuickLinks,
    },
  },
  {
    path: Path.game(s2GameId(":gameId")).researchPublisher(s2PublisherId(":publisherId")).pathname,
    element: (
      <AppWithSideMenu>
        <PublisherResearchListAndDetail />
      </AppWithSideMenu>
    ),
    handle: {
      crumb: makeBreadcrumbs("Publisher Research", ({ gameId }) => Path.game(gameId).research(), [
        MyGames,
        GamesBreadcrumb,
      ]),
      prefer: "right",
      quickLinks: GameQuickLinks,
    },
  },
];
